/**
 * Homepage Carousels Configuration
 *
 * This file defines the configuration for homepage carousels that display
 * learning modules based on different query types. Each carousel configuration includes:
 * - title: i18n translation key for the carousel title
 * - queryType: The type of query to make (see SUPPORTED_HOMEPAGE_QUERY_TYPES)
 * - tagIds: Array of tag IDs to query for modules (for TAGS query type)
 * - weblab: Optional per-carousel weblab gating
 *
 * Marketplace visibility is controlled by NalandaCMS (module active/inactive state per marketplace).
 * Locale visibility is controlled by SUOS (filters modules without instances in user's language).
 * The frontend only needs to handle weblab gating per carousel.
 */

import { WeblabId, WeblabTreatment } from 'src/constants/weblab';

// Supported query types for homepage carousels
export const SUPPORTED_HOMEPAGE_QUERY_TYPES = {
  TAGS: 'TAGS',
  // Future types can be added here
  // POPULAR: 'POPULAR',
  // NEW: 'NEW',
} as const;

export type SupportedHomepageQueryType =
  typeof SUPPORTED_HOMEPAGE_QUERY_TYPES[keyof typeof SUPPORTED_HOMEPAGE_QUERY_TYPES];

export interface HomepageCarouselWeblabConfig {
  weblabId: WeblabId;
  treatments: WeblabTreatment[];
}

export interface HomepageCarouselConfig {
  title: string; // Required i18n translation key for carousel title
  description?: string; // Optional i18n translation key for carousel description
  queryType: SupportedHomepageQueryType;
  tagIds?: string[]; // Optional for future query types that might not need tag IDs
  // Per-carousel weblab gating
  // If defined → carousel only visible when user's treatment is in the list
  // If undefined → no weblab check, carousel always eligible to render
  weblab?: HomepageCarouselWeblabConfig;
}

export interface HomepageCarouselsConfig {
  [key: string]: HomepageCarouselConfig;
}

// All tagIds added by queryType TAGS here will be used in Prod API calls
// Please make sure to validate the tagIds before adding it here
// To test config in beta, use the tagId: "fbf77b95-fc88-4c56-8526-524626341677"
export const HOMEPAGE_CAROUSELS: HomepageCarouselsConfig = {
  'homepage-prime-day-holiday': {
    title: 'homepage-advertise-and-grow-title',
    description: 'homepage-advertise-and-grow-description',
    queryType: 'TAGS',
    tagIds: ['f7378dd4-45dd-455b-9d94-500c5908ebec'], // Prod tagId
  },
  'homepage-carousel-peer-to-peer': {
    title: 'homepage-carousel-peer-to-peer-title',
    description: 'homepage-carousel-peer-to-peer-description',
    queryType: 'TAGS',
    tagIds: ['09b83c3c-ecdc-44c7-b83a-9d0c795a226a'], // Prod tagId
  },
};

export type HomepageCarouselId = keyof typeof HOMEPAGE_CAROUSELS;
